import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-[#221812]/20 bg-[#FCFAF4] px-4 py-3.5 text-base text-[#221812] placeholder-[#A39A8D] outline-none transition-colors duration-300 focus:border-[#9A7B3F] focus:ring-1 focus:ring-[#9A7B3F]";

const BUDGETS = ["Under $250", "$250 – $500", "$500 – $1,000", "$1,000+", "Prefer to discuss"];

const EMPTY = {
  full_name: "", email: "", phone: "", city_country: "", line: "", item_type: "",
  size_notes: "", timeline: "", vision: "", budget_range: "", reference_links: "",
  contact_method: "email", website: "",
};

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-2 block text-xs uppercase tracking-[0.25em] text-[#55504A]">{children}</label>
);

export default function ApplicationForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, { ...form, budget_range: form.budget_range || null });
      setDone(form);
      toast.success("Your custom application has been received.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" className="relative border-t border-[#221812]/10 bg-[#ECE5D6] px-6 py-28 sm:px-12 sm:py-36 lg:px-20" data-testid="application-section">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <Eyebrow>[ 05 — Custom Application ]</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 font-display text-3xl tracking-tight text-[#221812] sm:text-5xl" data-testid="application-heading">
            One-of-one. <span className="italic text-[#5E1A22]">By application.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-sm leading-relaxed text-[#55504A] sm:text-base">
            Applications are reviewed individually. Submission does not guarantee approval, availability, price, or delivery date. We will contact you before any payment or design commitment.
          </p>
        </Reveal>

        <Reveal delay={0.25} className="mt-12">
          {done ? (
            <div className="border border-[#9A7B3F]/50 bg-[#F9F5EB] p-8 sm:p-10" data-testid="application-success">
              <div className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#5E1A22]/50">
                  <Check className="h-5 w-5 text-[#5E1A22]" />
                </span>
                <h3 className="font-display text-xl text-[#221812] sm:text-2xl">Application received, {done.full_name}.</h3>
              </div>
              <dl className="mt-8 space-y-3 text-sm text-[#55504A]">
                <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">Email</dt><dd className="text-[#221812]">{done.email}</dd></div>
                <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">Line</dt><dd>{done.line === "street" ? "Street Apparel" : "Couture"}</dd></div>
                <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">Item type</dt><dd>{done.item_type}</dd></div>
                <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">City / Country</dt><dd>{done.city_country}</dd></div>
                <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">Contact via</dt><dd className="capitalize">{done.contact_method}</dd></div>
                {done.budget_range && <div className="flex gap-3"><dt className="w-36 shrink-0 text-[#8C857B]">Budget</dt><dd>{done.budget_range}</dd></div>}
              </dl>
              <p className="mt-8 border-t border-[#221812]/10 pt-6 text-xs leading-relaxed text-[#8C857B]">
                This is a request for a design conversation — not an order, approval, or reservation. Toriano reviews each application personally and will reach out before anything is agreed.
              </p>
              <button
                onClick={() => { setDone(null); setForm(EMPTY); }}
                className="mt-6 text-xs uppercase tracking-[0.3em] text-[#5E1A22] underline decoration-[#5E1A22]/40 underline-offset-8 transition-colors duration-300 hover:text-[#221812]"
                data-testid="application-add-another-btn"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="application-form">
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="ap-website">Leave this field empty</label>
                <input id="ap-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="ap-name">Full name *</Label>
                  <input id="ap-name" type="text" required maxLength={120} value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Your full name" data-testid="application-name-input" />
                </div>
                <div>
                  <Label htmlFor="ap-email">Email *</Label>
                  <input id="ap-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="application-email-input" />
                </div>
                <div>
                  <Label htmlFor="ap-phone">Phone <span className="text-[#8C857B]">(optional)</span></Label>
                  <input id="ap-phone" type="tel" maxLength={40} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Not required" data-testid="application-phone-input" />
                </div>
                <div>
                  <Label htmlFor="ap-city">City / Country *</Label>
                  <input id="ap-city" type="text" required maxLength={120} value={form.city_country} onChange={set("city_country")} className={inputCls} placeholder="e.g. Chicago, USA" data-testid="application-city-input" />
                </div>
                <div>
                  <Label htmlFor="ap-line">Preferred line *</Label>
                  <select id="ap-line" required value={form.line} onChange={set("line")} className={inputCls} data-testid="application-line-select">
                    <option value="" disabled>Select a line</option>
                    <option value="street">SANZONE Street Apparel</option>
                    <option value="couture">SANZONE Couture</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="ap-item">Item type *</Label>
                  <input id="ap-item" type="text" required maxLength={120} value={form.item_type} onChange={set("item_type")} className={inputCls} placeholder="e.g. Jacket, gown, hoodie" data-testid="application-item-type-input" />
                </div>
                <div>
                  <Label htmlFor="ap-size">Size / measurements <span className="text-[#8C857B]">(optional at this stage)</span></Label>
                  <input id="ap-size" type="text" maxLength={300} value={form.size_notes} onChange={set("size_notes")} className={inputCls} placeholder="Share only if relevant" data-testid="application-size-input" />
                </div>
                <div>
                  <Label htmlFor="ap-timeline">Occasion / desired timeline <span className="text-[#8C857B]">(optional)</span></Label>
                  <input id="ap-timeline" type="text" maxLength={200} value={form.timeline} onChange={set("timeline")} className={inputCls} placeholder="e.g. Event in autumn 2027" data-testid="application-timeline-input" />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="ap-vision">Creative vision *</Label>
                <textarea id="ap-vision" required rows={5} maxLength={2000} value={form.vision} onChange={set("vision")} className={`${inputCls} resize-y`} placeholder="Describe the piece you imagine — mood, references, details." data-testid="application-vision-textarea" />
              </div>

              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="ap-budget">Budget range <span className="text-[#8C857B]">(optional)</span></Label>
                  <select id="ap-budget" value={form.budget_range} onChange={set("budget_range")} className={inputCls} data-testid="application-budget-select">
                    <option value="">Prefer not to say</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="ap-links">Reference links <span className="text-[#8C857B]">(optional)</span></Label>
                  <input id="ap-links" type="text" maxLength={500} value={form.reference_links} onChange={set("reference_links")} className={inputCls} placeholder="Paste links separated by commas" data-testid="application-links-input" />
                </div>
              </div>

              <fieldset className="mt-8">
                <legend className="mb-3 text-xs uppercase tracking-[0.25em] text-[#55504A]">Preferred contact method *</legend>
                <div className="flex gap-3">
                  {["email", "phone"].map((m) => (
                    <label
                      key={m}
                      className={`cursor-pointer border px-4 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                        form.contact_method === m
                          ? "border-[#5E1A22] bg-[#5E1A22]/5 text-[#5E1A22]"
                          : "border-[#221812]/20 text-[#55504A] hover:border-[#221812]/50"
                      }`}
                    >
                      <input type="radio" name="contact_method" className="sr-only" checked={form.contact_method === m} onChange={() => setForm((f) => ({ ...f, contact_method: m }))} data-testid={`application-contact-${m}`} />
                      {m}
                    </label>
                  ))}
                </div>
              </fieldset>

              <p className="mt-8 text-xs leading-relaxed text-[#8C857B]">
                Never send payment details, ID documents, or sensitive personal information through this form.
              </p>

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 w-full border border-[#221812] px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#221812] transition-colors duration-300 hover:bg-[#221812] hover:text-[#F4EEE3] disabled:opacity-50 sm:w-auto"
                data-testid="application-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit Custom Application"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
